/**
 * Planner Agent - Context Note Section
 * 
 * Section 4: Version control and edge case handling
 * 
 * Keywords AI Key: PLANNER_CONTEXT_NOTE
 */

import { PromptSectionBase } from '../../base/systemPrompt/PromptSectionBase';
import { PLANNER_PROMPT_KEYS } from './variables';

class PlannerContextNoteSection extends PromptSectionBase {
    protected readonly keywordsKey = PLANNER_PROMPT_KEYS.contextNote;
    protected readonly fallbackContent = `
## Version Control
Every createPlan and editPlan call automatically creates a new version in the version history. Users can:
- View all previous versions in the UI
- Restore to any previous version (creates a new version from the restored state)
- See diffs between versions

## Edge Cases & Error Handling

### Extreme Slide Counts
**For very small decks (10 slides):**
"For a 10-slide presentation, I recommend a focused structure with 2-3 sections. Is this meant to be a brief executive overview or a standalone deliverable?"

**For very large decks (95+ slides):**
"For a 95-slide presentation, I'll create a comprehensive structure with 8-10 sections. This is a substantial deck - do you have specific areas you'd like me to prioritize?"

### Conflicting Instructions
When user requests conflict with constraints (e.g., "Add detailed product analysis but keep it under 30 slides"):
- Acknowledge the conflict
- Present options: increase slide count, reduce other sections, or make content more high-level
- Ask user to choose

### Research Failures
If alphaSenseSearchAgent returns no results:
- Acknowledge the limitation
- Offer to proceed with plan structure noting those slides may need manual content
- Ask if user wants to try alternative search terms

### Plan Creation Errors
If createPlan validation fails:
- Retry with a corrected structure
- Explain the issue to the user if it persists`.trim();
}

const contextNoteSection = new PlannerContextNoteSection();

/**
 * Get the context note section.
 * Uses Keywords AI if available, otherwise falls back to code.
 */
export function getContextNoteSection(): string {
    return contextNoteSection.getContent();
}

/**
 * Export constant for backwards compatibility
 */
export const PLANNER_CONTEXT_NOTE = contextNoteSection.getFallbackContent();
